import { Children, Fragment, isValidElement, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The app's dropdown. A drop-in for a native <select> (same `value`, `onChange(e)` with
 * `e.target.value`, and <option> children) that looks like the toolbar's Sort/Filter menus,
 * renders above drawers and dialogs, and works with the keyboard and on touch screens.
 */
export interface AppSelectChange { target: { value: string; name?: string }; currentTarget: { value: string; name?: string } }

interface Item { value: string; label: string; disabled: boolean }

const text = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(text).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return text(node.props.children);
  return "";
};

const collect = (children: ReactNode, out: Item[] = []): Item[] => {
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: string | number; disabled?: boolean; children?: ReactNode }>(child)) return;
    if (child.type === Fragment) { collect(child.props.children, out); return; }
    if (child.type === "option") {
      const label = text(child.props.children).replace(/\s+/g, " ").trim();
      out.push({ value: child.props.value === undefined ? label : String(child.props.value), label, disabled: !!child.props.disabled });
    }
  });
  return out;
};

export default function AppSelect({ value, onChange, children, className, disabled, required, name, placeholder, "aria-label": ariaLabel, title }: {
  value?: string | number | null;
  onChange?: (e: AppSelectChange) => void;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  placeholder?: string;
  "aria-label"?: string;
  title?: string;
}) {
  const items = useMemo(() => collect(children), [children]);
  const current = value === null || value === undefined ? "" : String(value);
  // Like a native select, an unknown value shows the first option.
  const selected = items.find((i) => i.value === current) ?? (placeholder ? undefined : items[0]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchable = items.length > 8;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(Math.max(0, items.findIndex((i) => i.value === current)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) setActive(0); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open, shown]);

  const choose = (item: Item | undefined) => {
    if (!item || item.disabled) return;
    setOpen(false);
    if (item.value !== current) onChange?.({ target: { value: item.value, name }, currentTarget: { value: item.value, name } });
  };

  const move = (step: number) => {
    if (!shown.length) return;
    let i = active;
    for (let n = 0; n < shown.length; n++) {
      i = (i + step + shown.length) % shown.length;
      if (!shown[i].disabled) break;
    }
    setActive(i);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(shown.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); choose(shown[active]); }
    else if (!searchable && e.key.length === 1 && /\S/.test(e.key)) {
      const k = e.key.toLowerCase();
      const start = active + 1;
      const hit = [...shown.slice(start), ...shown.slice(0, start)].find((i) => i.label.toLowerCase().startsWith(k));
      if (hit) setActive(shown.indexOf(hit));
    }
  };

  return (
    <>
    {(required || name) && (
      // Keeps native form validation ("required") and form serialization working.
      <input tabIndex={-1} aria-hidden="true" name={name} required={required} value={current} onChange={() => {}} className="sr-only" />
    )}
    <PopoverPrimitive.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <button
          type="button"
          data-app-select=""
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          title={title}
          onKeyDown={(e) => { if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { e.preventDefault(); setOpen(true); } }}
          className={cn(
            "inline-flex min-w-0 items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-xs text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-60",
            className,
          )}
        >
          <span className={cn("truncate", !selected && "opacity-60")}>{selected?.label || placeholder || " "}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 opacity-60 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          onKeyDown={onKey}
          onOpenAutoFocus={(e) => { if (!searchable) { e.preventDefault(); listRef.current?.focus(); } }}
          style={{ minWidth: "max(var(--radix-popover-trigger-width), 11rem)", maxWidth: "min(22rem, calc(100vw - 2rem))" }}
          className="z-[100002] rounded-xl border border-gray-200 bg-white p-2 text-gray-700 shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          {searchable && (
            <div className="mb-1 flex items-center gap-2 rounded-lg border border-gray-200 px-2.5">
              <Search className="h-3.5 w-3.5 text-gray-400" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…"
                className="w-full bg-transparent py-2 text-xs text-gray-900 outline-none placeholder:text-gray-400" />
            </div>
          )}
          <div ref={listRef} role="listbox" tabIndex={-1} className="overflow-y-auto overscroll-contain outline-none"
            style={{ maxHeight: "min(18rem, calc(var(--radix-popover-content-available-height) - 3.5rem))" }}>
            {shown.map((item, i) => (
              <button
                key={`${item.value}-${i}`}
                type="button"
                role="option"
                data-index={i}
                aria-selected={item.value === current}
                disabled={item.disabled}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(item)}
                className={cn(
                  "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                  i === active ? "bg-gray-50 text-gray-900" : "text-gray-700",
                  item.value === current && "font-medium text-gray-900",
                )}
              >
                <span className="truncate">{item.label || " "}</span>
                {item.value === current && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
              </button>
            ))}
            {!shown.length && <p className="px-3 py-2 text-xs text-gray-400">No matches</p>}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
    </>
  );
}
